// Fil du récit : événements dans l'ordre (anciens en haut), séparateurs de jour, une carte par type, écriture
// progressive du dernier récit du Conteur. Le fil n'a pas de hauteur propre : c'est le corps du Récit qui défile.
// Le fil se cale sur son dernier événement au montage (et à chaque retour sur l'onglet), puis suit les nouveautés
// tant que le lecteur est en bas ; s'il a remonté, « ↓ nouveaux récits » compte ce qui arrive.
import type { ComponentChildren } from "preact";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import type { DiceRollRecord, EventKind, GameEvent, Id } from "../../shared/types";
import { HOUR, MINUTE } from "../../shared/types";
import { parisDayKey } from "../../engine/state";
import "./Feed.css";

export interface FeedProps { events: GameEvent[]; myCharacterId: Id | null; now: number }

const DAY = 24 * HOUR;
/** Écriture progressive : un caractère toutes les 25 ms, soit environ 40 caractères par seconde. */
const TYPE_DELAY_MS = 25;
/** Seul un récit de moins de 30 secondes s'écrit lettre à lettre. */
const FRESH_MS = 30_000;
/** Marge (px) sous laquelle le lecteur est considéré « en bas » du fil. */
const BOTTOM_SLACK = 40;

const ICONS: Record<EventKind, string> = {
  narration: "📜", action: "", jet: "", combat: "⚔️", systeme: "", voyage: "🧭", rencontre: "✨",
  mort: "⚰️", niveau: "⬆️", butin: "🎁", chronique: "🗞️", monde: "🌙", sollicitation: "❓", repos: "🕯️", donjon: "🗝️",
};

/** Récits déjà écrits lettre à lettre : un récit ne se réécrit pas quand on change d'onglet. */
const typedIds = new Set<Id>();

const timeFmt = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", hour: "2-digit", minute: "2-digit" });
const shortDayFmt = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", day: "numeric", month: "short" });
const dayFmt = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", weekday: "long", day: "numeric", month: "long" });
const dayYearFmt = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", weekday: "long", day: "numeric", month: "long", year: "numeric" });
const fullFmt = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

function reducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/** Numéro du jour civil à Paris : la différence de deux numéros donne l'écart en jours. */
function dayIndex(at: number): number {
  return Date.parse(`${parisDayKey(at)}T00:00:00Z`) / DAY;
}

/** « Aujourd'hui », « Hier » ou la date complète, ex. « mardi 27 octobre ». */
function dayLabel(at: number, now: number): string {
  const gap = dayIndex(now) - dayIndex(at);
  if (gap === 0) return "Aujourd'hui";
  if (gap === 1) return "Hier";
  const sameYear = parisDayKey(at).slice(0, 4) === parisDayKey(now).slice(0, 4);
  return (sameYear ? dayFmt : dayYearFmt).format(at);
}

/** Horodatage relatif : « à l'instant », « il y a 12 min », « il y a 3 h », « hier à 21:30 ». */
function relativeTime(at: number, now: number): string {
  const diff = Math.max(0, now - at);
  if (diff < MINUTE) return "à l'instant";
  if (diff < HOUR) return `il y a ${Math.floor(diff / MINUTE)} min`;
  const gap = dayIndex(now) - dayIndex(at);
  if (gap <= 0) return `il y a ${Math.floor(diff / HOUR)} h`;
  const time = timeFmt.format(at);
  if (gap === 1) return `hier à ${time}`;
  return `le ${shortDayFmt.format(at)} à ${time}`;
}

/** Résultat d'un jet en une ligne, ex. « Perception 17 vs DD 13 — réussite ». */
function describeRoll(r: DiceRollRecord): string {
  const dc = r.dc !== undefined ? ` vs DD ${r.dc}` : "";
  const mode = r.mode === "avantage" ? " (avantage)" : r.mode === "desavantage" ? " (désavantage)" : "";
  let outcome = "";
  if (r.crit === "succes") outcome = "réussite critique";
  else if (r.crit === "echec") outcome = "échec critique";
  else if (r.success !== undefined) outcome = r.success ? "réussite" : "échec";
  return `${r.label} ${r.total}${dc}${mode}${outcome ? ` — ${outcome}` : ""}`;
}

function rollTone(r: DiceRollRecord): "ok" | "ko" | "" {
  if (r.crit === "succes" || r.success === true) return "ok";
  if (r.crit === "echec" || r.success === false) return "ko";
  return "";
}

/** Texte du Conteur : le **gras** est reconnu ; les retours à la ligne sont gérés par la CSS. */
function Rich({ text }: { text: string }) {
  return (
    <>
      {text.split(/\*\*([\s\S]+?)\*\*/g).map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}
    </>
  );
}

function DiceLines({ dice }: { dice: DiceRollRecord[] }) {
  return (
    <ul class="feed-dice">
      {dice.map((r, i) => {
        const tone = rollTone(r);
        return <li key={i} class={tone ? `feed-die is-${tone}` : "feed-die"}>🎲 {describeRoll(r)}</li>;
      })}
    </ul>
  );
}

interface TypedProps { id: Id; text: string; live: boolean; onProgress: () => void }

/** Récit du Conteur : écrit lettre à lettre s'il est le plus récent et frais, puis figé. */
function Typed({ id, text, live, onProgress }: TypedProps) {
  const [wanted] = useState(() => live && !reducedMotion() && !typedIds.has(id));
  const [n, setN] = useState(() => (wanted ? 0 : text.length));
  const typing = wanted && live && n < text.length;

  useEffect(() => {
    if (wanted) typedIds.add(id);
  }, []);

  useEffect(() => {
    if (!typing) return;
    const timer = setInterval(() => setN((k) => Math.min(text.length, k + 1)), TYPE_DELAY_MS);
    return () => clearInterval(timer);
  }, [typing, text.length]);

  useEffect(() => {
    if (typing) onProgress();
  }, [n, typing]);

  const visible = typing ? n : text.length;
  const lettrine = /^[\p{L}«“‘"]/u.exec(text)?.[0] ?? "";
  const dropped = lettrine !== "" && visible >= lettrine.length;
  const from = dropped ? lettrine.length : 0;
  return (
    <p class="feed-text feed-story">
      {dropped && <span class="feed-drop-cap">{lettrine}</span>}
      <Rich text={text.slice(from, visible)} />
      {typing && <span class="feed-caret" aria-hidden="true" />}
    </p>
  );
}

interface EntryProps { ev: GameEvent; mine: boolean; now: number; live: boolean; onProgress: () => void }

function Entry({ ev, mine, now, live, onProgress }: EntryProps) {
  const icon = ev.icon ?? ICONS[ev.kind];
  const title = ev.title ?? (ev.kind === "chronique" ? "Gazette" : null);
  const dice = ev.dice ?? [];
  return (
    <article class={`feed-item feed-${ev.kind}${mine ? " is-mine" : ""}`}>
      {ev.kind === "action" ? (
        <span class="feed-dot" aria-hidden="true" />
      ) : (
        icon && <span class="feed-icon" aria-hidden="true">{icon}</span>
      )}
      <div class="feed-body">
        {title && <h3 class="feed-title">{title}</h3>}
        {ev.kind === "narration" ? (
          <Typed id={ev.id} text={ev.text} live={live} onProgress={onProgress} />
        ) : (
          ev.text && <p class="feed-text"><Rich text={ev.text} /></p>
        )}
        {dice.length > 0 && <DiceLines dice={dice} />}
        <time class="feed-time" dateTime={new Date(ev.at).toISOString()} title={fullFmt.format(ev.at)}>
          {relativeTime(ev.at, now)}
        </time>
      </div>
    </article>
  );
}

/** Premier ancêtre qui défile : le fil n'a pas de hauteur propre, c'est le corps du Récit qui fait défiler. */
function scrollerOf(el: HTMLElement): HTMLElement | null {
  for (let p = el.parentElement; p; p = p.parentElement) {
    const oy = getComputedStyle(p).overflowY;
    if (oy === "auto" || oy === "scroll") return p;
  }
  return document.scrollingElement as HTMLElement | null;
}

/** Écart entre le bas du fil et le bas du cadre : 0 = calé en bas, positif = du fil reste sous le cadre. */
function gapBelow(list: HTMLElement, scroller: HTMLElement): number {
  return list.getBoundingClientRect().bottom - scroller.getBoundingClientRect().bottom;
}

/** Cale le bas du fil sur le bas du cadre : le dernier événement reste visible au-dessus de la barre d'action. */
function alignBottom(list: HTMLElement, scroller: HTMLElement, behavior: ScrollBehavior): void {
  const gap = gapBelow(list, scroller);
  if (Math.abs(gap) < 1) return;
  scroller.scrollTo({ top: scroller.scrollTop + gap, behavior });
}

/** Le lecteur est en bas s'il voit la fin du fil à BOTTOM_SLACK près, ou si tout tient dans le cadre. */
function nearBottom(list: HTMLElement, scroller: HTMLElement): boolean {
  if (scroller.scrollHeight <= scroller.clientHeight + 1) return true;
  return Math.abs(gapBelow(list, scroller)) <= BOTTOM_SLACK;
}

export function Feed(props: FeedProps) {
  const { events, myCharacterId, now } = props;
  const section = useRef<HTMLElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLElement | null>(null);
  const stuck = useRef(true);
  const prevLast = useRef<Id | null | undefined>(undefined);
  const [unread, setUnread] = useState(0);

  const follow = useCallback(() => {
    const l = list.current;
    const s = scroller.current;
    if (l && s && stuck.current) alignBottom(l, s, "auto");
  }, []);

  // Montage (et retour sur l'onglet Récit) : le fil commence en bas. Ensuite le cadre suit ses changements de taille
  // (texte qui s'écrit, polices chargées, barre d'action qui grandit) tant que le lecteur est en bas.
  useLayoutEffect(() => {
    const l = list.current;
    const s = section.current ? scrollerOf(section.current) : null;
    if (!l || !s) return;
    scroller.current = s;
    stuck.current = true;
    alignBottom(l, s, "auto");

    const onScroll = () => {
      if (s.clientHeight === 0) return; // cadre masqué : on garde le dernier état connu
      stuck.current = nearBottom(l, s);
      if (stuck.current) setUnread((u) => (u > 0 ? 0 : u));
    };
    s.addEventListener("scroll", onScroll, { passive: true });
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(follow) : null;
    observer?.observe(l);
    observer?.observe(s);
    return () => {
      s.removeEventListener("scroll", onScroll);
      observer?.disconnect();
    };
  }, []);

  // Nouveaux événements : suivre le fil si le lecteur est en bas, sinon compter les nouveautés.
  const lastId = events.length > 0 ? events[events.length - 1].id : null;
  useLayoutEffect(() => {
    const before = prevLast.current;
    prevLast.current = lastId;
    if (before === undefined || before === lastId) return; // premier rendu : déjà calé au montage
    const idx = before === null ? -1 : events.findIndex((e) => e.id === before);
    const added = idx < 0 ? events.length : events.length - 1 - idx;
    if (added <= 0) return;
    if (stuck.current) follow();
    else setUnread((u) => u + added);
  }, [lastId]);

  // Une seule carte s'écrit lettre à lettre : le dernier récit du Conteur, s'il est frais.
  let latestStory: GameEvent | undefined;
  for (let i = events.length - 1; i >= 0; i--) {
    if (events[i].kind === "narration") {
      latestStory = events[i];
      break;
    }
  }
  const liveId = latestStory && now - latestStory.at < FRESH_MS ? latestStory.id : null;

  const jump = () => {
    const l = list.current;
    const s = scroller.current;
    stuck.current = true;
    setUnread(0);
    if (l && s) alignBottom(l, s, reducedMotion() ? "auto" : "smooth");
  };

  const items: ComponentChildren[] = [];
  let day = "";
  for (const ev of events) {
    const key = parisDayKey(ev.at);
    if (key !== day) {
      day = key;
      items.push(
        <div key={`jour-${key}`} class="feed-day"><span>{dayLabel(ev.at, now)}</span></div>,
      );
    }
    items.push(
      <Entry
        key={ev.id}
        ev={ev}
        mine={myCharacterId !== null && ev.kind === "action" && ev.actorId === myCharacterId}
        now={now}
        live={ev.id === liveId}
        onProgress={follow}
      />,
    );
  }

  return (
    <section class="feed" ref={section} aria-label="Fil du récit">
      <div class="feed-scroll" ref={list} role="log" aria-live="polite">
        {events.length === 0 ? <p class="feed-empty">Le récit commence ici…</p> : items}
      </div>
      {unread > 0 && (
        <div class="feed-jump-zone">
          <button type="button" class="btn btn-small feed-jump" onClick={jump}>↓ nouveaux récits</button>
        </div>
      )}
    </section>
  );
}
